import React from 'react';

import { Button, Grid, Typography } from '@mui/material';

import { useCreateUserPasswordChangeRequestMutation } from 'src/api';
import { useNotification } from 'src/hooks/app';
import { useScreenInfo } from 'src/hooks/common';
import { useModal } from 'src/hooks/shared';
import { useCurrentUser } from 'src/hooks/user';

interface UserCreatePasswordChangeRequestModalContentProps {
  onCloseModal: () => void;
  switchClosability: ReturnType<typeof useModal>['switchClosability'];
}

export const UserCreatePasswordChangeRequestModalContent: React.FC<
  UserCreatePasswordChangeRequestModalContentProps
> = ({ onCloseModal, switchClosability }) => {
  const { isMobile } = useScreenInfo();
  const { createApolloContext } = useNotification();

  const { user: currentUser } = useCurrentUser();

  const [createUserPasswordChangeRequestMutation, { loading: requestSending }] =
    useCreateUserPasswordChangeRequestMutation({
      context: createApolloContext({
        successMessage: 'Change Password Request Created Successfully. Check Your Email!',
      }),
    });

  const onSendRequestClick = React.useCallback(async () => {
    switchClosability(false);
    try {
      // The request is refused for SSO-only accounts, which have no password. The error is toasted
      // by the global handler, so keep the modal open and let the user close it.
      await createUserPasswordChangeRequestMutation();
      onCloseModal();
    } catch (error) {
      console.error(error);
    } finally {
      switchClosability(true);
    }
  }, [createUserPasswordChangeRequestMutation, onCloseModal, switchClosability]);

  return (
    <Grid container spacing={isMobile ? 2 : 4}>
      <Grid size={12}>
        <Typography variant="body1">
          You will have to check {currentUser?.email} for confirmation and changing your password
        </Typography>
      </Grid>
      <Grid container spacing={3} justifyContent="center" alignItems="center" size={12}>
        <Grid>
          <Button
            color="error"
            variant="outlined"
            onClick={onCloseModal}
            disabled={requestSending}
            sx={{ px: isMobile ? 4 : 'auto' }}
          >
            Cancel
          </Button>
        </Grid>
        <Grid>
          <Button
            variant="contained"
            onClick={onSendRequestClick}
            disabled={requestSending}
            sx={{ px: isMobile ? 4 : 'auto' }}
          >
            Yes, send
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};
